import React from "react";
import Hero from "@/components/Hero";
import FAQ from "@/components/FAQ";
import BookingCTA from "@/components/BookingCTA";
import Marquee from "@/components/Marquee";
import { Manifesto, EditorialSplit, RelatedLinks } from "@/components/Editorial";
import { Reveal, Stagger, staggerItem } from "@/components/Reveal";
import SectionLabel from "@/components/SectionLabel";
import { motion } from "framer-motion";
import { HERO_IMG, PHOTO } from "@/data/site";

const brands = [
    {
        n: "01",
        name: "Australian Gold",
        line: "Abbronzatura",
        desc: "La linea è pensata per chi desidera accompagnare i momenti di esposizione e prendersi cura della pelle con prodotti specifici per la propria routine estiva.",
        hue: "bg-champagne/70",
        img: PHOTO.donnaRilassata,
    },
    {
        n: "02",
        name: "Cuccio",
        line: "Cura del corpo",
        desc: "Applicare con costanza una crema corpo aiuta a trasformare un semplice gesto quotidiano in un momento di benessere personale.",
        hue: "bg-rose-powder/70",
        img: PHOTO.curaPelle,
    },
    {
        n: "03",
        name: "Robeus",
        line: "Skincare viso & corpo",
        desc: "La gamma disponibile comprende prodotti pensati per accompagnare la skincare quotidiana e rispondere a diverse esigenze estetiche.",
        hue: "bg-sage/30",
        img: PHOTO.curaViso,
    },
];

export default function Prodotti() {
    return (
        <>
            <Hero
                eyebrow="Prodotti professionali per la tua beauty routine"
                title={["Prodotti Viso,", "Corpo & Abbronzatura."]}
                subtitle="Una selezione di prodotti professionali scelti per accompagnare la cura della pelle anche a casa, con il consiglio attento e personalizzato di Isa."
                subtitleMobile="Prodotti professionali per la tua beauty routine."
                imgDesktop={HERO_IMG.prodotti.d}
                imgMobile={HERO_IMG.prodotti.m}
                primaryCta={{ label: "Richiedi informazioni", href: "/contatti" }}
                secondaryCta={{ label: "Contattaci", href: "/contatti" }}
            />

            <div className="bg-ivory pt-8 pb-4"></div>

            <Manifesto
                number="01"
                eyebrow="A casa"
                title="Prenditi cura della tua pelle anche a casa."
                body={[
                    "La cura della pelle non termina con il trattamento effettuato nel centro estetico. Una routine quotidiana scelta con attenzione può aiutare a mantenere la pelle curata e piacevole al tatto.",
                    "Non proponiamo prodotti scelti casualmente o una gamma troppo ampia. Preferiamo selezionare alcune linee professionali e consigliare ogni cliente in base alle sue esigenze.",
                    "L'obiettivo è rendere la beauty routine più semplice, piacevole e adatta alla persona.",
                ]}
            />

            {/* Brands */}
            <section className="relative py-24 md:py-32 bg-ivory-warm">
                <div className="mx-auto max-w-[1440px] px-6 md:px-12">
                    <div className="grid lg:grid-cols-12 gap-8 mb-16">
                        <div className="lg:col-span-4">
                            <Reveal>
                                <SectionLabel number="02" text="I marchi" />
                            </Reveal>
                        </div>
                        <div className="lg:col-span-8">
                            <Reveal delay={0.1}>
                                <h2 className="font-display text-[32px] md:text-[46px] leading-[1.08] text-ink">
                                    Tre marchi, tre attenzioni differenti.
                                </h2>
                            </Reveal>
                            <Reveal delay={0.2}>
                                <p className="mt-6 text-ink-soft text-[16px] leading-relaxed max-w-2xl">
                                    Nel centro estetico abbiamo scelto tre
                                    marchi dedicati a esigenze differenti. La
                                    disponibilità dei singoli prodotti può
                                    variare — contatta Spazio Isa per conoscere
                                    la disponibilità attuale.
                                </p>
                            </Reveal>
                        </div>
                    </div>

                    <Stagger className="grid md:grid-cols-3 gap-6">
                        {brands.map((b, i) => (
                            <motion.article
                                key={b.name}
                                variants={staggerItem}
                                className={`group relative overflow-hidden rounded-[28px] ${b.hue} p-8 md:p-10 min-h-[440px] flex flex-col`}
                            >
                                <div className="flex items-start justify-between">
                                    <span className="text-ink-muted text-[15px] md:text-[17px] tabular-nums font-semibold">
                                        {b.n}
                                    </span>
                                    <span className="text-[11px] md:text-[12px] uppercase tracking-[0.24em] text-ink-muted bg-white/60 rounded-full px-3 py-1">
                                        {b.line}
                                    </span>
                                </div>
                                <h3 className="mt-14 font-display text-[28px] md:text-[36px] leading-[1.08] text-ink">
                                    {b.name}
                                </h3>
                                <p className="mt-5 text-ink-soft text-[14.5px] leading-relaxed">
                                    {b.desc}
                                </p>
                                <div className="mt-auto pt-8">
                                    <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                                        <img
                                            src={b.img}
                                            alt=""
                                            loading="lazy"
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                        />
                                    </div>
                                </div>
                            </motion.article>
                        ))}
                    </Stagger>
                </div>
            </section>

            <EditorialSplit
                number="03"
                eyebrow="Con te"
                title="Un consiglio pensato per te, non per tutti."
                image={PHOTO.dialogo}
                imageAlt="Consulenza prodotti"
                accentBg="bg-rose-powder/40"
                body={[
                    "Ogni pelle ha caratteristiche ed esigenze differenti. Un prodotto adatto a una persona potrebbe non essere la scelta ideale per un'altra.",
                    "Prima di consigliare una crema o un prodotto per l'abbronzatura, ascoltiamo le necessità della cliente e valutiamo insieme quale soluzione inserire nella routine.",
                    "Da Spazio Isa la vendita non è separata dalla consulenza. Ogni prodotto viene proposto come parte di un percorso di cura personale.",
                ]}
            />

            <BookingCTA
                title="Vuoi un consiglio sui prodotti?"
                subtitle="Ti aiuteremo a scegliere la soluzione più adatta alle tue esigenze, accompagnandoti nella cura quotidiana con prodotti professionali selezionati."
            />

            <FAQ
                items={[
                    {
                        q: "Quali marchi sono disponibili?",
                        a: "Australian Gold per l'abbronzatura, Cuccio per la cura del corpo e Robeus per la cura del viso e del corpo.",
                    },
                    {
                        q: "Posso acquistare senza fare un trattamento?",
                        a: "Sì. Puoi contattarci o passare in centro per ricevere informazioni sui prodotti disponibili e scegliere quello più adatto.",
                    },
                    {
                        q: "Come faccio a scegliere il prodotto giusto?",
                        a: "Isa ti aiuterà a individuare il prodotto più adatto in base alle caratteristiche della tua pelle, alle tue abitudini e ai risultati desiderati.",
                    },
                    {
                        q: "I prodotti sono professionali?",
                        a: "Sì. Spazio Isa seleziona marchi professionali scelti per offrire qualità e accompagnare la cura della pelle anche a casa.",
                    },
                    {
                        q: "Posso ricevere consigli sull'utilizzo?",
                        a: "Certamente. Al momento dell'acquisto riceverai indicazioni su come utilizzare correttamente il prodotto.",
                    },
                    {
                        q: "La disponibilità è sempre la stessa?",
                        a: "La disponibilità può variare nel tempo. Ti consigliamo di contattarci per conoscere i prodotti presenti in centro.",
                    },
                ]}
            />

            <RelatedLinks
                items={[
                    { label: "Estetica", to: "/estetica" },
                    { label: "Pressoterapia", to: "/pressoterapia" },
                    { label: "Epilazione Laser", to: "/laser" },
                    { label: "Infrarossi", to: "/infrarossi" },
                    { label: "Radiofrequenza", to: "/radiofrequenza" },
                    { label: "Contatti", to: "/contatti" },
                ]}
            />
        </>
    );
}
